/**
 * Lightweight tracking utilities.
 * - captureUTM(): persists UTM params from URL to localStorage (first-touch)
 * - trackEvent(): pushes to GA4 dataLayer if present; noop otherwise
 * - getAttribution(): returns stored UTMs for appending to lead forms
 * - withTracking(): wraps onClick handlers to log a named event
 */

type EventPayload = Record<string, unknown>;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "fbclid",
  "msclkid",
] as const;

const STORAGE_KEY = "hive_attr_v1";
const FIRST_TOUCH_KEY = "hive_attr_first_v1";

export function captureUTM(): void {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  const hits: Record<string, string> = {};
  UTM_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v) hits[k] = v;
  });
  if (Object.keys(hits).length === 0) return;
  const enriched = {
    ...hits,
    _captured_at: new Date().toISOString(),
    _landing: window.location.pathname,
    _referrer: document.referrer || "",
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(enriched));
    if (!localStorage.getItem(FIRST_TOUCH_KEY)) {
      localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(enriched));
    }
  } catch {
    // storage may be unavailable (privacy mode)
  }
}

export function getAttribution(): Record<string, string> {
  if (typeof window === "undefined") return {};
  try {
    const current = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    const first = JSON.parse(localStorage.getItem(FIRST_TOUCH_KEY) || "{}");
    const out: Record<string, string> = { ...current };
    Object.entries(first).forEach(([k, v]) => {
      out[`first_${k}`] = String(v);
    });
    return out;
  } catch {
    return {};
  }
}

export function trackEvent(name: string, payload: EventPayload = {}): void {
  if (typeof window === "undefined") return;
  const enriched = { event: name, ...payload, _ts: Date.now() };
  try {
    (window.dataLayer ||= []).push(enriched);
  } catch {
    // noop
  }
  if (typeof window.gtag === "function") {
    try {
      window.gtag("event", name, payload);
    } catch {
      // noop
    }
  }
  if (window.location.hostname === "localhost") {
    // eslint-disable-next-line no-console
    console.log("[trackEvent]", name, payload);
  }
}

export function withTracking<T extends (...args: unknown[]) => unknown>(
  fn: T | undefined,
  name: string,
  payload: EventPayload = {}
): T {
  return ((...args: Parameters<T>) => {
    trackEvent(name, payload);
    return fn ? fn(...args) : undefined;
  }) as T;
}
